import type { HistoryItem } from "@/types";
import extractExternalLinks from "@/lib/extractExternalLinks";
import { Fragment } from "react";

type TMessageItem = {
  item: HistoryItem;
  index: number;
};

export default function MessageItem({ item, index }: TMessageItem) {
  const formatTimestamp = (timestamp: Date): string => {
    return timestamp.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };
  const renderMessageWithLinks = (text: string) => {
    const externalLinks = extractExternalLinks(text);

    if (externalLinks.length === 0) {
      return text;
    }

    // Find all link positions in the text and sort them by position
    const linkPositions: Array<{
      link: typeof externalLinks[0];
      startIndex: number;
      endIndex: number;
      searchText: string;
    }> = [];

    externalLinks.forEach((link) => {
      const searchText = link.originalText || link.value;
      let searchIndex = 0;

      // Find all occurrences of this link text
      while (true) {
        const linkIndex = text.indexOf(searchText, searchIndex);
        if (linkIndex === -1) break;

        // Check if this position is already covered by another link
        const isOverlapping = linkPositions.some(pos =>
          linkIndex < pos.endIndex && (linkIndex + searchText.length) > pos.startIndex
        );

        if (!isOverlapping) {
          linkPositions.push({
            link,
            startIndex: linkIndex,
            endIndex: linkIndex + searchText.length,
            searchText
          });
        }

        searchIndex = linkIndex + 1;
      }
    });

    // Sort by position in text
    linkPositions.sort((a, b) => a.startIndex - b.startIndex);

    // Split text into parts and replace links
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let elementKey = 0;

    linkPositions.forEach((linkPos) => {
      // Add text before the link
      if (linkPos.startIndex > lastIndex) {
        parts.push(text.substring(lastIndex, linkPos.startIndex));
      }

      // Add the clickable link
      const href = linkPos.link.type === "email"
        ? `mailto:${linkPos.link.value}`
        : linkPos.link.type === "phone"
          ? `tel:${linkPos.link.value.replace(/[^+\d]/g, '')}`
          : linkPos.link.value;

      parts.push(
        <a
          key={`link-${elementKey++}`}
          href={href}
          target={linkPos.link.type === "url" ? "_blank" : "_self"}
          rel={linkPos.link.type === "url" ? "noopener noreferrer" : undefined}
          className="text-primary hover:text-primary-foreground underline"
        >
          {linkPos.searchText}
        </a>
      );

      lastIndex = linkPos.endIndex;
    });

    // Add remaining text after the last link
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.map((part, index) => (
      <Fragment key={index}>{part}</Fragment>
    ));
  };

  if (item.transcript === "") return null;
  return (
    <div key={index}>
      <div
        className={`max-w-3/4 flex flex-col rounded-lg p-4 shadow ${item.sender === "user" ? "ml-auto pl-4" : ""}`}
        style={{
          backgroundColor: `var(${item.sender === "user" ? "--user-msg-bg" : "--bot-msg-bg"})`,
          color: `var(${item.sender === "user" ? "--user-msg-text" : "--bot-msg-text"})`,
        }}
      >
        <p className="text-sm">{renderMessageWithLinks(item.transcript)}</p>
      </div>
      <div className="mt-2 text-center text-xs text-gray-500">{formatTimestamp(item.timestamp)}</div>
    </div>
  );
}
